import { createContext, useState, useEffect, useContext, Dispatch, SetStateAction, ReactNode } from 'react';
import { updateActiveTask, Util } from '../../util/Util';
import { contactFromDetails } from '../client/contactBlock';
import { Item } from '../../util/Item';
import { catalogMinSellPrice } from '../../util/salePrice';
import { v4 as uuidv4 } from 'uuid';
import { BatchTask } from "../../util/Batch";
import { useGridApiRef } from "@mui/x-data-grid";
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { useLocation } from 'react-router-dom';
import { SelectedPayments } from '../payments/PaymentModal';
import { useUI } from '../../providers/MoleUIContext';
import { useAppSocket } from '../../providers/AppSocketContext';
import { businessToday } from "../../util/businessClock";

import { addMoney } from "../../util/quantity";
export interface SaleTask<T> extends BatchTask<T> {
	agent: number | null;
	payment: SelectedPayments;
	source: number;
	issueChange?: boolean;
	offerId?: string;
}

interface ContextProps {
	pendingTasks: SaleTask<Item>[];
	setPendingTasks: Dispatch<SetStateAction<SaleTask<Item>[]>>;
	fetchedTask: any | null;
	setFetchedTask: (task: any) => void;
	addNewTask: () => void;
	activeTask: SaleTask<Item> | undefined;
	items: any[];
	setItems: Dispatch<SetStateAction<any[]>>;
	/** Barcode map for catalog (shared across Classic / Visual / Split so layout switches keep cache). */
	barcodes: Record<string, number>;
	setBarcodes: Dispatch<SetStateAction<Record<string, number>>>;
	/** Depot id items/barcodes were last loaded for (null = never). */
	itemsSourceId: number | null;
	setItemsSourceId: Dispatch<SetStateAction<number | null>>;
	clients: any[];
	setClients: Dispatch<SetStateAction<any[]>>;
	/** Active payment modes cache (shared so layout switches don't re-fetch). */
	paymentModes: any[];
	setPaymentModes: Dispatch<SetStateAction<any[]>>;
	clientSituation: any | null;
	setClientSituation: (situation: any) => void;
	agentSituation: any | null;
	setAgentSituation: (situation: any) => void;
	isCreditLoading: boolean;
	setIsCreditLoading: (situation: any) => void;
	isAgentCreditLoading: boolean;
	setIsAgentCreditLoading: (situation: any) => void;
	useClientCredit: boolean;
	useAgentCredit: boolean;
	useBothCredit: boolean;
	apiRef: ReturnType<typeof useGridApiRef<GridApiCommunity>>;
	shortCircuit: () => { shouldBypass: boolean; type: "client" | "agent" | "both" | "N/A" };
	findAccount: (id: number) => any;
	accountIsUnlimited: (account: any) => boolean;
}

export const SaleContext = createContext<ContextProps | undefined>(undefined);

interface ProviderProps {
	children: ReactNode;
}

export const SaleProvider = ({ children }: ProviderProps) => {
	const [pendingTasks, setPendingTasks] = useState<SaleTask<Item>[]>([]);
	const [fetchedTask, setFetchedTask] = useState<any>(null);
	const [items, setItems] = useState<any[]>([]);
	const [barcodes, setBarcodes] = useState<Record<string, number>>({});
	const [itemsSourceId, setItemsSourceId] = useState<number | null>(null);
	const [clients, setClients] = useState<any[]>([]);
	const [paymentModes, setPaymentModes] = useState<any[]>([]);
	const [clientSituation, setClientSituation] = useState<any>(null);
	const [agentSituation, setAgentSituation] = useState<any>(null);
	const [isCreditLoading, setIsCreditLoading] = useState<boolean>(false);
	const [isAgentCreditLoading, setIsAgentCreditLoading] = useState<boolean>(false);
	const apiRef = useGridApiRef();
	const activeTask = pendingTasks?.find((task) => task.active);

	const { appSettings } = useAppSocket();

	const useClientCredit = Util.compareStrings(appSettings?.CREDIT_POLICY?.value || "NONE", "CLIENT");
	const useAgentCredit = Util.compareStrings(appSettings?.CREDIT_POLICY?.value || "NONE", "AGENT");
	const useBothCredit = Util.compareStrings(appSettings?.CREDIT_POLICY?.value || "BOTH", "BOTH");

	const { activeDepot } = useUI();

	const findAccount = (id: number) => clients.find((client) => client.id === id);

	const accountIsUnlimited = (account: any) => account?.credit_limit < 0;

	const shortCircuit = (): { shouldBypass: boolean; type: "client" | "agent" | "both" | "N/A" } => {
		const clientBypass = (): boolean => {
			const client = findAccount(activeTask?.receiver || -1)
			return accountIsUnlimited(client);
		}

		const agentBypass = (): boolean => {
			const agent = findAccount(activeTask?.agent || -1)
			return accountIsUnlimited(agent);
		}

		if (activeTask?.receiver === 1) { // no need to fetch balances, walk-in customer must pay all
			return {
				shouldBypass: true,
				type: "N/A"
			};
		} else {
			if (useClientCredit) {
				return {
					shouldBypass: clientBypass(),
					type: "client"
				};
			} else if (useAgentCredit) {
				if ((activeTask?.agent ?? null) === null) {
					// Under this policy the agent's limit is the only one that
					// governs, so a credit sale with no agent has nothing to
					// check against. Mirrors the backend, which refuses it.
					return {
						shouldBypass: false,
						type: "agent"
					};
				} else if (activeTask?.agent === activeTask?.receiver) {
					return {
						shouldBypass: clientBypass(),
						type: "client"
					};
				} else {
					return {
						shouldBypass: agentBypass(),
						type: "agent"
					};
				}
			} else if (useBothCredit) {
				if ((activeTask?.agent ?? null) === null || activeTask?.agent === activeTask?.receiver) {
					return {
						shouldBypass: clientBypass(),
						type: "client"
					};
				} else {
					if (clientBypass() || agentBypass()) {
						return {
							shouldBypass: true,
							type: "both"
						};
					} else {
						return {
							shouldBypass: false,
							type: "both"
						};
					}
				}
			}
		}

		return {
			shouldBypass: false,
			type: "N/A"
		};
	}

	const addNewTask = () => {
		const newTask: SaleTask<Item> = {
			id: uuidv4(),
			items: [],
			active: true,
			date: businessToday(),
			reference: Util.getInvoiceNumber(),
			receiver: 1,
			source: activeDepot,
			description: "",
			contact: null,
			payment: {},
			agent: null,
			is_public: false,
			server_v: 0,
			createdAt: new Date(),
			updatedAt: new Date(),
			total: function () {
				return this.items!.reduce((sum, item) => addMoney(sum, item.total), 0);
			},
		};

		setPendingTasks((prevTasks) =>
			prevTasks.map((task) => ({ ...task, active: false })).concat(newTask)
		);
	};

	useEffect(() => {
		if (fetchedTask) {
			const itemRecords = fetchedTask.records.map((record: any) => {
				const item = new Item(
					{
						id: record.item_id,
						item_code: record.item_code,
						item: record.item_name,
						category: record.category,
						price: record.amount,
						quantity: record.quantity,
						isFractional: record.is_fractional === true || record.IsFractional === true || items.find((c: any) => c.id === record.item_id)?.IsFractional === true,
						minSellPrice: catalogMinSellPrice(items.find((c: any) => c.id === record.item_id)),
					}
				);

				return item;
			});

			const { batch, date, client_id, agent_id, reference, description, src_id, offer_id } = fetchedTask.details;
			updateActiveTask(setPendingTasks, {
				...activeTask,
				id: fetchedTask.offerPosId ? activeTask?.id : batch,
				items: itemRecords,
				date: date,
				receiver: client_id,
				agent: agent_id,
				source: fetchedTask.offerPosId || src_id,
				reference: reference,
				description: description,
				offerId: offer_id || undefined,
				contact: contactFromDetails(fetchedTask.details),
			});
		}
	}, [fetchedTask]);

	const value: ContextProps = {
		pendingTasks,
		setPendingTasks,
		fetchedTask,
		setFetchedTask,
		addNewTask,
		activeTask,
		items,
		setItems,
		barcodes,
		setBarcodes,
		itemsSourceId,
		setItemsSourceId,
		clients,
		setClients,
		paymentModes,
		setPaymentModes,
		clientSituation,
		setClientSituation,
		agentSituation,
		setAgentSituation,
		isCreditLoading,
		setIsCreditLoading,
		isAgentCreditLoading,
		setIsAgentCreditLoading,
		useClientCredit,
		useAgentCredit,
		useBothCredit,
		apiRef,
		shortCircuit,
		findAccount,
		accountIsUnlimited,
	};

	return (
		<SaleContext.Provider value={value}>
			{children}
		</SaleContext.Provider>
	);
};

export const useSale = () => {
	const context = useContext(SaleContext);

	if (!context) {
		throw new Error('useSale must be used within a SaleProvider');
	}
	return context;
};